<template>
    <div class="px-4 py-2 ">
        <div class="grid grid-cols-1 gap-4">
            <div class="main-bg-color p-4 rounded-lg shadow">
                <h3 class="font-semibold  mb-4">
                    Conversion Metrics for {{ advertiser.advertiserName }}
                </h3>
                <div class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <!-- Click to Signup Rate -->
                    <div class="card-bg p-4 rounded-lg">
                        <div class="flex items-center justify-between mb-2">
                            <span class=" font-medium">Click to Signup Rate</span>
                            <i class="pi pi-info-circle text-gray-400 cursor-help"
                                v-tooltip.bottom="'Percentage of clicks that result in signups'" />
                        </div>
                        <div class="text-2xl font-bold text-blue-600">
                            {{ calculateRate(advertiser.signups, advertiser.clicks) }}%
                        </div>
                        <div class="text-sm text-gray-500 mt-1">
                            {{ advertiser.signups }} signups from {{ advertiser.clicks }} clicks
                        </div>
                    </div>

                    <!-- Signup to FTD Rate -->
                    <div class="card-bg p-4 rounded-lg">
                        <div class="flex items-center justify-between mb-2">
                            <span class="font-medium">Signup to FTD Rate</span>
                            <i class="pi pi-info-circle text-gray-400 cursor-help"
                                v-tooltip.bottom="'Percentage of signups that become First Time Depositors'" />
                        </div>
                        <div class="text-2xl font-bold text-green-600">
                            {{ calculateRate(advertiser.FTD, advertiser.signups) }}%
                        </div>
                        <div class="text-sm text-gray-500 mt-1">
                            {{ advertiser.FTD }} FTDs from {{ advertiser.signups }} signups
                        </div>
                    </div>

                    <!-- Click to FTD Rate -->
                    <div class="card-bg p-4 rounded-lg">
                        <div class="flex items-center justify-between mb-2">
                            <span class="font-medium">Click to FTD Rate</span>
                            <i class="pi pi-info-circle text-gray-400 cursor-help"
                                v-tooltip.bottom="'Overall conversion rate from clicks to First Time Depositors'" />
                        </div>
                        <div class="text-2xl font-bold text-purple-600">
                            {{ calculateRate(advertiser.FTD, advertiser.clicks) }}%
                        </div>
                        <div class="text-sm text-gray-500 mt-1">
                            {{ advertiser.FTD }} FTDs from {{ advertiser.clicks }} clicks
                        </div>
                    </div>

                    <!-- Deposit Rate -->
                    <div v-if="advertiser.deposits > 0" class="card-bg p-4 rounded-lg">
                        <div class="flex items-center justify-between mb-2">
                            <span class="font-medium">Deposit Rate</span>
                            <i class="pi pi-info-circle text-gray-400 cursor-help"
                                v-tooltip.bottom="'Average number of deposits per FTD'" />
                        </div>
                        <div class="text-2xl font-bold text-orange-600">
                            {{ calculateRate(advertiser.deposits, advertiser.FTD, false) }}
                        </div>
                        <div class="text-sm text-gray-500 mt-1">
                            {{ advertiser.deposits }} deposits from {{ advertiser.FTD }} FTDs
                        </div>
                    </div>
                </div>
            </div>

            <!-- Chart Section -->
            <div v-if="selectedMonth !== null" class="main-bg-color p-4 rounded-lg shadow">
                <h3 class="font-semibold  mb-4 flex items-center gap-2">
                    Daily Performance for {{ formatMonthYear(selectedMonth) }}
                    <i class="pi pi-info-circle  cursor-help"
                        v-tooltip.bottom="'Click on metrics in the legend to show/hide them from the chart'" />
                </h3>

                <div class="relative h-[300px]">
                    <!-- Loading Overlay -->
                    <div v-if="isLoading" class="absolute inset-0 card-bg bg-opacity-80 z-10 
                                flex items-center justify-center">
                        <ProgressSpinner strokeWidth="4" class="w-16 h-16 card-bg" animationDuration=".7s" />
                    </div>

                    <!-- Chart -->
                    <Chart v-if="chartData" type="line" :data="chartData" :options="chartOptions"
                        class="h-full card-bg rounded-lg" />

                    <!-- Debug info -->
                    <pre v-if="!chartData && !isLoading" class="text-red-500">
                        No chart data available
                    </pre>
                </div>
            </div>
            <pre v-if="selectedMonth === null" class="text-red-500 text-center">
                Select a specific month from above to view daily stats
            </pre>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue';
import Chart from 'primevue/chart';
import ProgressSpinner from 'primevue/progressspinner';
import { formatMonthYear } from '@/utils/dateHelper';
import EarningsService from '@/services/EarningsService';
const props = defineProps({
    advertiser: {
        type: Object,
        required: true
    },
    selectedMonth: {
        type: Date,
        required: true
    }
});

const isLoading = ref(false);
const chartData = ref(null);

const chartOptions = ref({
    maintainAspectRatio: false,
    aspectRatio: 0.6,
    plugins: {
        legend: {
            position: 'top',
            labels: {
                usePointStyle: true,
                font: { size: 12 },
                color: 'white'
            }
        },
        tooltip: {
            mode: 'index',
            intersect: false
        }
    },
    scales: {
        x: {
            ticks: {
                font: { size: 11 },
                color: 'white'
            },
            grid: {
                display: false,
                color: '#565555'
            }
        },
        y: {
            beginAtZero: true,
            ticks: {
                font: { size: 11 },
                color: 'white'
            },
            grid: {
                color: '#565555'
            }
        }
    },
    interaction: {
        intersect: false,
        mode: 'index'
    }
});

const fetchDailyStats = async () => {
    isLoading.value = true;
    try {
        const response = await EarningsService.getAdvertiserDailyStats(
            props.advertiser.advertiserId,
            props.selectedMonth
        );
        
        // Sort by date to ensure correct order
        const sortedData = response.data.sort((a, b) => new Date(a.date) - new Date(b.date));


        chartData.value = {
            labels: sortedData.map(item => new Date(item.date).getDate()),
            datasets: [
                {
                    label: 'Clicks',
                    data: sortedData.map(item => item.clicks),
                    borderColor: '#2196F3',
                    tension: 0.4,
                    fill: false
                },
                {
                    label: 'Signups',
                    data: sortedData.map(item => item.signups),
                    borderColor: '#4CAF50',
                    borderDash: [5, 5],
                    tension: 0.4,
                    fill: false
                },
                {
                    type: 'bar',
                    label: 'FTDs',
                    data: sortedData.map(item => item.FTD),
                    backgroundColor: '#FF9800',
                    borderColor: '#FF9800',
                    borderWidth: 1,
                    borderRadius: 4,
                    barPercentage: 0.5,
                    categoryPercentage: 0.5
                }
            ]
        };

    } catch (error) {
        console.error('Error fetching daily stats:', error);
    } finally {
        isLoading.value = false;
    }
};

// Fetch data when month changes
watch(() => props.selectedMonth, (newValue) => {
    if (newValue !== null) {
        fetchDailyStats();
    }
});

// Initial fetch
onMounted(() => {
    if (props.selectedMonth !== null) {
        fetchDailyStats();
    }
});

onUnmounted(() => {
    chartData.value = null;
});

const calculateRate = (numerator, denominator, asPercentage = true) => {
    if (!denominator) return '0';
    const rate = numerator / denominator;
    return asPercentage
        ? (rate * 100).toFixed(2)
        : rate.toFixed(2);
};
</script>

<style scoped>
.pi-info-circle:hover {
    color: #4B5563;
    /* gray-600 */
}
</style>